import { Injectable } from '@angular/core';
import {
  AuthStateChange,
  ConfirmPasswordResetOptions,
  CreateUserWithEmailAndPasswordOptions,
  GetIdTokenOptions,
  GetIdTokenResult,
  LinkResult,
  LinkWithEmailAndPasswordOptions,
  SendPasswordResetEmailOptions,
  SignInResult,
  SignInWithEmailAndPasswordOptions,
  UnlinkOptions,
  UnlinkResult,
  UpdateEmailOptions,
  UpdatePasswordOptions,
  UpdateProfileOptions,
  User,
} from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import {
  GoogleAuthProvider,
  getAuth,
  signInWithCredential,
} from 'firebase/auth';
import { Observable, ReplaySubject, lastValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  CapacitorFirebaseAuthenticationService,
  CapacitorFirebaseCrashlyticsService,
} from '../../capacitor';
import { HeapService } from '../../heap/heap.service';
import { ChatService } from '../../chat/chat.service';
import { LoggingService } from '../../logging/logging.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthenticationService {
  private readonly currentUserSubject = new ReplaySubject<User | undefined>(1);
  public redirectUrl = '';

  constructor(
    private readonly capacitorFirebaseAuthenticationService: CapacitorFirebaseAuthenticationService,
    private readonly capacitorFirebaseCrashlyticsService: CapacitorFirebaseCrashlyticsService,
    private readonly chatService: ChatService,
    private readonly heapService: HeapService,
  ) {
    this.capacitorFirebaseAuthenticationService.authStateChange$.subscribe(
      (event) => {
        this.currentUserSubject.next(event.user || undefined);

        if (event && event.user) {
          // Initialize Intercom and Heap without arbitrary delay.
          // ChatService.initialize() handles its own retry logic and
          // guards against concurrent calls, so fire-and-forget is safe.
          this.chatService.initialize(event.user).catch((error) => {
            console.error(
              '[FirebaseAuth] Intercom initialization failed',
              error,
            );
          });

          this.heapService.identify(event.user.uid, {
            email: event.user?.email,
          });

          void this.setFirebaseCrashlyticsUserId(event.user?.uid);
        }
      },
    );
  }

  public get authStateChange$(): Observable<AuthStateChange> {
    return this.capacitorFirebaseAuthenticationService.authStateChange$;
  }

  public get currentUser$(): Observable<User | undefined> {
    return this.currentUserSubject.asObservable();
  }

  public get currentUserId$(): Observable<string | undefined> {
    return this.currentUser$.pipe(map((user) => user?.uid));
  }

  public async confirmPasswordReset(
    options: ConfirmPasswordResetOptions,
  ): Promise<void> {
    await this.capacitorFirebaseAuthenticationService.confirmPasswordReset(
      options,
    );
  }

  public async createUserWithEmailAndPassword(
    options: CreateUserWithEmailAndPasswordOptions,
  ): Promise<SignInResult> {
    return await this.capacitorFirebaseAuthenticationService.createUserWithEmailAndPassword(
      options,
    );
  }

  public getCurrentUser(): Promise<User | undefined> {
    return lastValueFrom(this.currentUser$.pipe(take(1)));
  }

  public getCurrentUserId(): Promise<string | undefined> {
    return lastValueFrom(this.currentUserId$.pipe(take(1)));
  }

  public async getRedirectResult(): Promise<SignInResult | undefined> {
    if (Capacitor.isNativePlatform()) {
      return;
    }
    return this.capacitorFirebaseAuthenticationService.getRedirectResult();
  }

  public sendEmailVerification(): Promise<void> {
    return this.capacitorFirebaseAuthenticationService.sendEmailVerification();
  }

  public sendPasswordResetEmail(
    options: SendPasswordResetEmailOptions,
  ): Promise<void> {
    return this.capacitorFirebaseAuthenticationService.sendPasswordResetEmail(
      options,
    );
  }

  public signInAnonymously(): Promise<SignInResult> {
    return this.capacitorFirebaseAuthenticationService.signInAnonymously();
  }

  public signInWithEmailAndPassword(
    options: SignInWithEmailAndPasswordOptions,
  ): Promise<SignInResult> {
    return this.capacitorFirebaseAuthenticationService.signInWithEmailAndPassword(
      options,
    );
  }

  public signInWithApple(): Promise<SignInResult> {
    return this.capacitorFirebaseAuthenticationService.signInWithApple();
  }

  public async signInWithGoogle(): Promise<SignInResult> {
    if (Capacitor.isNativePlatform()) {
      return this.capacitorFirebaseAuthenticationService.signInWithGoogle();
    }
    try {
      return await this.capacitorFirebaseAuthenticationService.signInWithGoogle(
        {
          mode: 'popup',
        },
      );
    } catch (error: any) {
      if (error?.code === 'auth/popup-blocked') {
        return this.capacitorFirebaseAuthenticationService.signInWithGoogle({
          mode: 'redirect',
        });
      }
      throw error;
    }
  }

  public async linkWithEmailAndPassword(
    options: LinkWithEmailAndPasswordOptions,
  ): Promise<LinkResult> {
    const result =
      await this.capacitorFirebaseAuthenticationService.linkWithEmailAndPassword(
        options,
      );
    this.currentUserSubject.next(result.user || undefined);
    return result;
  }

  public async linkWithApple(): Promise<LinkResult> {
    const result =
      await this.capacitorFirebaseAuthenticationService.linkWithApple();
    this.currentUserSubject.next(result.user || undefined);
    return result;
  }

  public async linkWithGoogle(): Promise<LinkResult> {
    if (Capacitor.isNativePlatform()) {
      const result =
        await this.capacitorFirebaseAuthenticationService.linkWithGoogle();
      this.currentUserSubject.next(result.user || undefined);
      return result;
    }
    try {
      const result =
        await this.capacitorFirebaseAuthenticationService.linkWithGoogle({
          mode: 'popup',
        });
      this.currentUserSubject.next(result.user || undefined);
      return result;
    } catch (error: any) {
      if (error?.code === 'auth/popup-blocked') {
        const result =
          await this.capacitorFirebaseAuthenticationService.linkWithGoogle({
            mode: 'redirect',
          });
        this.currentUserSubject.next(result.user || undefined);
        return result;
      }
      throw error;
    }
  }

  /**
   * Signs in using the OAuth credential extracted from an
   * `auth/credential-already-in-use` error, avoiding a second Google prompt.
   * Falls back to a full `signInWithGoogle()` on native platforms where
   * `GoogleAuthProvider.credentialFromError` is unavailable.
   */
  public async signInWithExistingCredential(error: any): Promise<void> {
    const oAuthCredential = Capacitor.isNativePlatform()
      ? null
      : GoogleAuthProvider.credentialFromError(error);

    if (oAuthCredential) {
      await signInWithCredential(getAuth(), oAuthCredential);
      const capUser =
        await this.capacitorFirebaseAuthenticationService.getCurrentUser();
      this.currentUserSubject.next(capUser.user || undefined);
    } else {
      await this.signInWithGoogle();
    }
  }

  /**
   * Continues with Google authentication from an anonymous user.
   * - Tries to link the Google account first (upgrade anonymous → Google).
   * - If the credential is already in use, signs in with the existing
   *   Google account silently.
   * Returns a structured result so the caller can decide whether to sync
   * the anonymous user's booking data.
   */
  public async continueWithGoogleFromAnonymous(): Promise<{
    result: SignInResult;
    action: 'linked' | 'signed_in_existing' | 'signed_in_new';
  }> {
    const currentUser = await this.getCurrentUser();

    if (!currentUser || !currentUser.isAnonymous) {
      const signInResult = await this.signInWithGoogle();
      return {
        result: signInResult,
        action: signInResult.additionalUserInfo?.isNewUser
          ? 'signed_in_new'
          : 'signed_in_existing',
      };
    }

    try {
      const linkResult = await this.linkWithGoogle();
      return { result: linkResult, action: 'linked' };
    } catch (error: any) {
      if (error?.code === 'auth/credential-already-in-use') {
        await this.signInWithExistingCredential(error);

        const capUser =
          await this.capacitorFirebaseAuthenticationService.getCurrentUser();
        const signInResult: SignInResult = {
          user: capUser.user,
          credential: null,
          additionalUserInfo: { isNewUser: false },
        };

        return { result: signInResult, action: 'signed_in_existing' };
      }

      throw error;
    }
  }

  public async unlink(options: UnlinkOptions): Promise<UnlinkResult> {
    const result =
      await this.capacitorFirebaseAuthenticationService.unlink(options);
    this.currentUserSubject.next(result.user || undefined);
    return result;
  }

  public signOut(): Promise<void> {
    return this.capacitorFirebaseAuthenticationService.signOut();
  }

  public getIdToken(options: GetIdTokenOptions): Promise<GetIdTokenResult> {
    return this.capacitorFirebaseAuthenticationService.getIdToken(options);
  }

  public async updateProfile(options: UpdateProfileOptions): Promise<void> {
    await this.capacitorFirebaseAuthenticationService.updateProfile(options);
    const result =
      await this.capacitorFirebaseAuthenticationService.getCurrentUser();
    this.currentUserSubject.next(result.user || undefined);
  }

  public async updateEmail(email: UpdateEmailOptions): Promise<void> {
    await this.capacitorFirebaseAuthenticationService.updateEmail(email);
    const result =
      await this.capacitorFirebaseAuthenticationService.getCurrentUser();
    this.currentUserSubject.next(result.user || undefined);
  }

  public async updatePassword(options: UpdatePasswordOptions): Promise<void> {
    await this.capacitorFirebaseAuthenticationService.updatePassword(options);
    const result =
      await this.capacitorFirebaseAuthenticationService.getCurrentUser();
    this.currentUserSubject.next(result.user || undefined);
  }

  public async deleteUser(): Promise<void> {
    await this.capacitorFirebaseAuthenticationService.deleteUser();
  }

  private setFirebaseCrashlyticsUserId(
    userId: string | undefined,
  ): Promise<void> {
    return this.capacitorFirebaseCrashlyticsService.setUserId({
      userId: userId || '',
    });
  }
}
